import { useState, useEffect } from "react";
import EventSelection from "./steps/EventSelection";
import YourDetails from "./steps/YourDetails";
import DelegateDetails from "./steps/DelegateDetails";
import TicketTypeSelection from "./steps/TicketTypeSelection";
import Confirmation from "./steps/Confirmation";
import ThankYou from "./steps/ThankYou";
import NoEventsAvailable from "./components/NoEventsAvailable";

import ProgressBar from "./components/ProgressBar";

const BookingForm = () => {
  // Get preselected event data from the DOM
  const getPreselectedEvent = () => {
    const formElement = document.getElementById('lde-booking-form');
    if (formElement && formElement.dataset.event) {
      try {
        return JSON.parse(formElement.dataset.event);
      } catch (e) {
        console.error('Error parsing preselected event data:', e);
        return null;
      }
    }
    return null;
  };

  // Check if we have multiple events data
  const hasMultipleEvents = () => {
    const formElement = document.getElementById('lde-booking-form');
    return formElement && formElement.dataset.events;
  };

  // Get event availability status from the DOM
  const getEventAvailabilityStatus = () => {
    const formElement = document.getElementById('lde-booking-form');
    if (formElement && formElement.dataset.noEventsStatus) {
      return formElement.dataset.noEventsStatus;
    }
    return null;
  };

  // Get custom message for no events from the DOM
  const getNoEventsMessage = () => {
    const formElement = document.getElementById('lde-booking-form');
    if (formElement && formElement.dataset.noEventsMessage) {
      return formElement.dataset.noEventsMessage;
    }
    return null;
  };

  // Check if we have a form restore data
  const getFormRestoreData = () => {
    //console.log('Checking for form restore data...');
    const formElement = document.getElementById('lde-booking-form');
    //console.log('Form element:', formElement);
    
    if (formElement) {
      // Log all data attributes
      //console.log('Form element data attributes:', formElement.dataset);
      
      // Try directly checking the attribute too
      const directAttribute = formElement.getAttribute('data-form-restore');
      //console.log('Direct attribute check for data-form-restore:', directAttribute);
      
      // List all attributes for debugging
      //console.log('All form element attributes:');
      for (let i = 0; i < formElement.attributes.length; i++) {
        //console.log(`  ${formElement.attributes[i].name}: ${formElement.attributes[i].value}`);
      }
      
      // Check specific dataset property
      //console.log('Form restore data attribute from dataset:', formElement.dataset.formRestore);
      
      if (formElement.dataset.formRestore) {
        try {
          const parsedData = JSON.parse(formElement.dataset.formRestore);
          //console.log('Successfully parsed form restore data:', parsedData);
          return parsedData;
        } catch (e) {
          console.error('Error parsing form restore data:', e);
          return null;
        }
      }
      
      // Try parsing the direct attribute as a fallback
      if (directAttribute) {
        try {
          //console.log('Trying to parse direct attribute as fallback');
          const parsedData = JSON.parse(directAttribute);
          //console.log('Successfully parsed form restore data from direct attribute:', parsedData);
          return parsedData;
        } catch (e) {
          console.error('Error parsing direct attribute:', e);
        }
      }
    }
    return null;
  };

  const preselectedEvent = getPreselectedEvent();
  const multipleEventsAvailable = hasMultipleEvents();
  const noEventsStatus = getEventAvailabilityStatus();
  const noEventsMessage = getNoEventsMessage();
  
  const restoreData = getFormRestoreData();
  //console.log('Initial restore data before state initialization:', restoreData);
  
  // Determine initial step based on restore data or preselected event
  let initialStep = 1;
  
  if (restoreData && Object.keys(restoreData).length > 0) {
    // If we have restore data, determine step based on the data
    if (restoreData.bookingFor) {
      const hasCompletedDelegates = 
        (restoreData.bookingFor === "Myself & others" || restoreData.bookingFor === "Others") && 
        restoreData.delegates && 
        restoreData.delegates.length > 0;
      
      initialStep = hasCompletedDelegates ? 5 : 
                   (restoreData.bookingFor === "Myself & others" || restoreData.bookingFor === "Others") ? 4 : 5;
    }
  } else if (preselectedEvent && !multipleEventsAvailable) {
    // If no restore data but we have a preselected event
    initialStep = 2;
  }
  
  //console.log('Initial step determined:', initialStep);
  
  // Initialize all state at once with correct values
  const [step, setStep] = useState(initialStep);
  
  // Add debugging to track the invoiceDetails
  //console.log('restoreData.invoiceDetails before state init:', restoreData?.invoiceDetails);
  
  const [formData, setFormData] = useState({
    // Start with preselected event if available
    event: (restoreData && restoreData.event) ? 
           (preselectedEvent ? { ...restoreData.event, ...preselectedEvent } : restoreData.event) : 
           preselectedEvent || "",
    
    // Use restore data if available, otherwise empty strings/arrays
    firstName: restoreData?.firstName || "",
    lastName: restoreData?.lastName || "",
    email: restoreData?.email || "",
    mobile: restoreData?.mobile || "",
    company: restoreData?.company || "",
    firmType: restoreData?.firmType || "",
    jobTitle: restoreData?.jobTitle || "",
    bookingFor: restoreData?.bookingFor || "",
    delegates: restoreData?.delegates || [],
    paymentMethod: restoreData?.paymentMethod || "stripe",
    ticketType: restoreData?.ticketType || "standard",
    // Explicitly include invoiceDetails
    invoiceDetails: restoreData?.invoiceDetails || {},
    booking_id: restoreData?.booking_id || null
  });
  
  //console.log('Initial form state after initialization:', formData);
  //console.log('invoiceDetails in initial form state:', formData.invoiceDetails);

  const bookingForOptions = [
    { key: "Myself", value: "Myself" },
    { key: "Myself & others", value: "Myself & others" },
    { key: "Others", value: "Others" },
  ];

  const showProgressBar = false;

  const showDelegateStep = formData.bookingFor === "Myself & others" || formData.bookingFor === "Others";

  // Calculate total steps based on preselected event and delegate step
  const getTotalSteps = () => {
    let total = 5; // Default: Event, Details, Ticket Type, Payment, Complete
    
    if (preselectedEvent && !multipleEventsAvailable) {
      total--; // Remove event selection step only if single event and preselected
    }
    
    if (showDelegateStep) {
      total++; // Add delegate step
    }
    
    return total;
  };

  const totalSteps = getTotalSteps();

  // Get current step for progress bar
  const getCurrentStep = () => {
    if (preselectedEvent && !multipleEventsAvailable) {
      // Adjust step number if event is preselected and we don't show multiple events
      return step - 1;
    }
    return step;
  };

  const nextStep = (paymentMethod) => {
    if (step === 5) { // Confirmation step
      setFormData(prev => ({
        ...prev,
        paymentMethod: paymentMethod
      }));
    }
    
    // Skip delegate step if not needed
    if (step === 2 && !showDelegateStep) {
      setStep(4); // Skip to ticket type selection
    } else {
      setStep(prev => prev + 1);
    }
  };

  const editStep = (stepNumber) => {
    setStep(stepNumber);
  };

  // Helper function to check if a step is completed
  const isStepCompleted = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return !!formData.event;
      case 2:
        return !!(formData.firstName && formData.lastName && formData.email && formData.mobile);
      case 3:
        return showDelegateStep ? formData.delegates.length > 0 : true;
      case 4:
        return !!formData.ticketType;
      case 5:
        return step > 5;
      default:
        return false;
    }
  };

  // Calculate the correct display number for each step
  const getStepDisplayNumber = (actualStepNumber) => {
    if (preselectedEvent && !multipleEventsAvailable) {
      // If there's a preselected event and we don't show multiple events, adjust the step numbers
      if (actualStepNumber === 2) return 1; // Your Details becomes step 1
      if (actualStepNumber === 3) return 2; // Delegate Details becomes step 2
      if (actualStepNumber === 4) return showDelegateStep ? 3 : 2; // Ticket Type becomes step 3 or 2
      if (actualStepNumber === 5) return showDelegateStep ? 4 : 3; // Payment becomes step 4 or 3
    } else if (!showDelegateStep) {
      // If delegate step is skipped, adjust step numbers
      if (actualStepNumber === 4) return 3; // Ticket Type becomes step 3
      if (actualStepNumber === 5) return 4; // Payment becomes step 4
    }
    return actualStepNumber;
  };

  // Handle form restoration if needed
  useEffect(() => {
    //console.log('Form restoration useEffect running');
    //console.log('restoreData:', restoreData);
    //console.log('preselectedEvent:', preselectedEvent);
    //console.log('Current form state:', formData);
    
    // Clear stored booking ID if we're on the initial steps (new booking)
    if (step <= 2) {
      try {
        // We only want to clear the booking ID if we're starting a new booking
        // not if we're resuming a previous session via restoreData
        if (!restoreData || (restoreData && !restoreData.booking_id)) {
          //console.log('Starting new booking - clearing stored booking ID');
          localStorage.removeItem('lde_booking_id');
        } else {
          //console.log('Resuming existing booking - keeping stored booking ID');
        }
      } catch (err) {
        console.error('Error accessing localStorage:', err);
      }
    }
    
    // We no longer need to set form data here as it's already initialized,
    // but we'll keep this useEffect for debugging purposes
    if (restoreData && Object.keys(restoreData).length > 0) {
      //console.log('Form has been restored with data from previous session');
    } else {
      //console.log('No restore data found, using default form state');
    }
  }, [restoreData, preselectedEvent, formData, step]);

  const renderStepHeader = (stepNumber, title) => {
    const isCompleted = isStepCompleted(stepNumber);
    const isActive = step === stepNumber;
    const isAccessible = stepNumber <= step;
    const displayNumber = getStepDisplayNumber(stepNumber);

    return (
      <div className={`accordion-header ${isActive ? 'active' : ''} ${isCompleted && !isActive ? 'completed' : ''}`}>
        <div className="accordion-title">
          <span className="step-number">{displayNumber}</span>
          <h2>{title}</h2>
        </div>
        {isCompleted && !isActive && isAccessible && (
          <button 
            className="edit-button"
            onClick={() => editStep(stepNumber)}
          >
            Edit
          </button>
        )}
      </div>
    );
  };

  // If there are no events available or all events are in the past
  if (noEventsStatus) {
    return <NoEventsAvailable status={noEventsStatus} message={noEventsMessage} />;
  }

  // Prepare form data for submission
  const prepareFormDataForSubmit = () => {
    // Log the formData before processing
    //console.log('formData before prepare:', formData);
    //console.log('invoiceDetails before prepare:', formData.invoiceDetails);
    //console.log('booking_id before prepare:', formData.booking_id);
    
    // Extract the needed event data for submission
    const { event, ticketType, booking_id, ...restFormData } = formData;
    let eventData = {};
    
    if (event) {
      // Get the correct price based on ticket type
      let price = event.price || 0;
      if (ticketType === 'early_bird' && event.earlyBirdPrice) {
        price = event.earlyBirdPrice;
      } else if (ticketType === 'local_authority' && event.localAuthorityPrice) {
        price = event.localAuthorityPrice;
      } else if (event.customTickets && Array.isArray(event.customTickets)) {
        // Check for custom ticket types
        const customTicket = event.customTickets.find(ticket => ticket.id === ticketType);
        if (customTicket && customTicket.price) {
          price = customTicket.price;
        }
      }
      
      eventData = {
        event_id: event.id,
        date_id: event.dateId,
        price: price,
        name: event.name,
        blogId: event.blogId || 1, // Ensure blog ID is included, default to 1 (main site) if missing
        ticketType: ticketType || 'standard',
        // Always include customTickets in the data sent to the server
        customTickets: event.customTickets || []
      };
      
      // Log custom tickets for debugging
      if (event.customTickets && Array.isArray(event.customTickets)) {
        //console.log('Including custom tickets in eventData:', event.customTickets);
      }
    }
    
    // Create a new object with all form data and formatted event data
    const preparedData = {
      ...restFormData, // Include ALL other form data (including invoiceDetails)
      event: eventData,
      ticketType: ticketType || 'standard'
    };
    
    // Include booking_id if it exists
    if (booking_id) {
      //console.log('Including booking_id in prepared data:', booking_id);
      preparedData.booking_id = booking_id;
    } else {
      // Check localStorage as a fallback
      try {
        const storedBookingId = localStorage.getItem('lde_booking_id');
        if (storedBookingId) {
          //console.log('Found booking_id in localStorage:', storedBookingId);
          preparedData.booking_id = parseInt(storedBookingId, 10);
        } else {
          //console.log('No booking_id found in form data or localStorage');
        }
      } catch (err) {
        console.error('Error accessing localStorage:', err);
      }
    }
    
    //console.log('Prepared form data:', preparedData);
    //console.log('invoiceDetails after prepare:', preparedData.invoiceDetails);
    //console.log('booking_id after prepare:', preparedData.booking_id);
    
    return preparedData;
  };

  return (
    <div className="booking-container">
      {showProgressBar && (
        <ProgressBar 
          step={getCurrentStep()} 
          showDelegateStep={showDelegateStep}
          totalSteps={totalSteps}
          hideEventStep={!!(preselectedEvent && !multipleEventsAvailable)}
        />
      )}
      <div className="booking-accordion">
        {/* Show EventSelection if no preselected event OR if we have multiple events to choose from */}
        {(!preselectedEvent || multipleEventsAvailable) && (
          <div className={`accordion-section ${step >= 1 ? 'visible' : 'future'}`}>
            {renderStepHeader(1, "Choose Event")}
            {step === 1 && (
              <div className="accordion-content">
                <EventSelection 
                  nextStep={nextStep} 
                  setFormData={setFormData} 
                  formData={formData} 
                />
              </div>
            )}
          </div>
        )}

        <div className={`accordion-section ${step >= 2 ? 'visible' : 'future'}`}>
          {renderStepHeader(2, "Your Details")}
          {step === 2 && (
            <div className="accordion-content">
              <YourDetails 
                nextStep={nextStep} 
                prevStep={() => setStep(1)} 
                setFormData={setFormData} 
                formData={formData} 
                bookingForOptions={bookingForOptions}
                hideBackButton={!!(preselectedEvent && !multipleEventsAvailable)}
              />
            </div>
          )}
        </div>

        {showDelegateStep && (
          <div className={`accordion-section ${step >= 3 ? 'visible' : 'future'}`}>
            {renderStepHeader(3, "Delegate Details")}
            {step === 3 && (
              <div className="accordion-content">
                <DelegateDetails 
                  nextStep={nextStep} 
                  prevStep={() => setStep(2)} 
                  setFormData={setFormData} 
                  formData={formData} 
                />
              </div>
            )}
          </div>
        )}

        <div className={`accordion-section ${step >= 4 ? 'visible' : 'future'}`}>
          {renderStepHeader(4, "Ticket Type")}
          {step === 4 && (
            <div className="accordion-content">
              <TicketTypeSelection 
                nextStep={nextStep} 
                prevStep={() => setStep(showDelegateStep ? 3 : 2)} 
                setFormData={setFormData} 
                formData={formData} 
              />
            </div>
          )}
        </div>

        <div className={`accordion-section ${step >= 5 ? 'visible' : 'future'}`}>
          {renderStepHeader(5, "Invoice Details")}
          {step === 5 && (
            <div className="accordion-content">
              {/* Prepare data directly here instead of calling the function */}
              <Confirmation 
                nextStep={nextStep} 
                prevStep={() => setStep(4)} 
                formData={formData} // Pass the raw formData instead of processed data
                setFormData={setFormData} // Pass the setFormData function to allow updating the booking_id
              />
            </div>
          )}
        </div>

        {step === 6 && <ThankYou />}
      </div>
    </div>
  );
};

export default BookingForm;
